import { ref, watch, type Ref } from 'vue'
import { useKevelDecision } from './useKevelDecision'
import { getKevelZoneConfig } from '../config/kevelZones'
import { parseKevelDecision, type ParsedKevelAd } from '../utils/parseKevelDecision'
import type { CategoryBreadcrumb } from '../../shared/keywords'

export interface UseKevelPlacementOptions {
    /** Element whose visibility triggers the decision request. */
    target: Ref<HTMLElement | null>
    /** Optional category breadcrumb for keyword targeting. */
    breadcrumb?: CategoryBreadcrumb
    /** Skip the placement entirely (no observer, no request). */
    skip?: boolean
}

export interface UseKevelPlacementOutput {
    /** Parsed ad once the decision arrives (null before that and on no-fill). */
    ad: Ref<ParsedKevelAd | null>
    /** Whether the decision request is in flight. */
    isLoading: Ref<boolean>
    /**
     * False when the placement is skipped (`skip`, unknown zone or no zone ID configured).
     * Hosts hide the slot then, so no empty space is reserved.
     */
    isEnabled: boolean
}

/**
 * Entry point for hard-coded (non-CMS) placements in host components: resolves the zone
 * config, requests the decision once `target` scrolls into view and parses the result.
 * The divName equals the zone name, so each zone must only be used once per page.
 */
export function useKevelPlacement(zone: string, { target, breadcrumb, skip = false }: UseKevelPlacementOptions): UseKevelPlacementOutput {
    const ad = ref<ParsedKevelAd | null>(null)

    // Unknown or disabled (no zone ID) zone: skip like `skip`
    const zoneConfig = skip ? null : getKevelZoneConfig(zone)
    if (zoneConfig == null) {
        return { ad, isLoading: ref(false), isEnabled: false }
    }

    const { decision, isLoading } = useKevelDecision({
        target,
        placement: {
            divName: zone,
            zoneIds: [zoneConfig.zoneId],
            adTypes: zoneConfig.adTypes,
        },
        aboveTheFold: false,
        breadcrumb,
    })

    // Guard against array responses (multi-winner placements are not supported here)
    watch(decision, (newDecision) => {
        if (newDecision != null && !Array.isArray(newDecision)) {
            ad.value = parseKevelDecision(newDecision)
        }
    }, { immediate: true })

    return { ad, isLoading, isEnabled: true }
}
